
import {
  defineComponent, onMounted, onUnmounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { confirmPlan } from '@/composables/billing/billingApi';

type Phase = 'verifying' | 'completed' | 'pending' | 'failed';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

/**
 * Landing page after the shop owner approves the Shopify recurring charge.
 *
 * Flow:
 *   1. Mount → call POST /billing/plans/confirm with the charge_id from query.
 *   2. If completed → wait 1.5s for the cron to apply the plan, then reload.
 *   3. If still pending → poll /billing/plans/confirm every 5s for up to 5 min.
 *   4. If failed → display the error.
 */
export default defineComponent({
  name: 'BillingShopifyReturn',

  components: { ProgressSpinner, Button, Message },

  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const phase = ref<Phase>('verifying');
    const errorMessage = ref<string | null>(null);
    const confirmationUrl = ref<string | null>(null);
    const chargeId = ref<string | null>(null);

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let stopAt = 0;

    const stopPolling = () => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const tryConfirm = async () => {
      const id = chargeId.value
        ?? (route.query.charge_id as string | undefined)
        ?? null;
      const result = await confirmPlan(id ? { chargeId: id } : {});

      if (result.status === 'completed') {
        phase.value = 'completed';
        stopPolling();
        // The cron applies the plan within ~1 minute; reload after a short
        // delay so the dashboard shows the active plan.
        setTimeout(() => {
          window.location.href = router.resolve({ name: 'shop-subscription' }).href;
          window.location.reload();
        }, 1500);
        return;
      }
      if (result.status === 'pending_shopify_approval') {
        confirmationUrl.value = result.confirmationUrl ?? null;
        chargeId.value = result.chargeId ?? chargeId.value;
        // Keep polling silently
        return;
      }
      if (result.status === 'failed') {
        phase.value = 'failed';
        errorMessage.value = result.error;
        stopPolling();
      }
    };

    const goToShopify = () => {
      if (confirmationUrl.value) {
        window.location.href = confirmationUrl.value;
      }
    };

    const backToBilling = () => {
      router.push({ name: 'shop-subscription' });
    };

    onMounted(async () => {
      stopAt = Date.now() + MAX_POLL_DURATION_MS;
      await tryConfirm();
      if (phase.value !== 'completed' && phase.value !== 'failed') {
        pollTimer = setInterval(async () => {
          if (Date.now() > stopAt) {
            phase.value = 'pending';
            stopPolling();
            return;
          }
          await tryConfirm();
        }, POLL_INTERVAL_MS);
      }
    });

    onUnmounted(() => stopPolling());

    return {
      t,
      phase,
      errorMessage,
      confirmationUrl,
      goToShopify,
      backToBilling,
    };
  },
});
